import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { NotifyService } from '@app/services';
import { EmployeeTimeOffPolicies } from '@models/time-off-v3/employee-time-off.policies';
import { CellDisplay } from '@table/enums/cell-display.enum';

const mapEmployeesToRow = (employeeTimeOffPolicy: EmployeeTimeOffPolicies): Row => {
    const employee = employeeTimeOffPolicy.simpleEmployee;
    const status = employee.scheduledForFuturePolicyAssignment
        ? 'time-off-v3.policyComponents.enrolled-employees.scheduled'
        : 'time-off-v3.policyComponents.enrolled-employees.enrolled';

    return {
        cells: [{ employee, isAvatar: true }, employeeTimeOffPolicy.effectiveAt ?? '', status],
    };
};

@Component({
    selector: 'app-enrolled-employees',
    templateUrl: './enrolled-employees.template.html',
    styleUrls: ['./enrolled-employees.style.scss'],
})
export class EnrolledEmployeesComponent implements OnInit {
    @Input() timeOffPolicy: TimeOffPolicy;

    isLoading = false;
    assignedEmployees: EmployeeTimeOffPolicies[] = [];
    columns: Columns[] = [
        { columnName: 'time-off-v3.policyComponents.enrolled-employees.nameColumn', columnSpan: 'columns-4' },
        {
            columnName: 'time-off-v3.policyComponents.enrolled-employees.effectiveDate',
            columnSpan: 'columns-4',
            columnType: CellDisplay.date,
        },
        { columnName: 'time-off-v3.policyComponents.enrolled-employees.status', columnSpan: 'columns-4' },
    ];

    rows: Row[] = [];

    constructor(
        private notify: NotifyService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    private async loadData(): Promise<void> {
        try {
            this.isLoading = true;
            const timeOffPolicy = await TimeOffPolicy.with([
                'currentNonTerminatedEmployeeTimeOffPolicies',
                'currentNonTerminatedEmployeeTimeOffPolicies.simpleEmployee',
                'futureAssignedNonTerminatedEmployeeTimeOffPolicies',
                'futureAssignedNonTerminatedEmployeeTimeOffPolicies.simpleEmployee',
            ]).find(this.route.snapshot.params.policy);

            this.timeOffPolicy = timeOffPolicy;

            this.assignedEmployees = this.combineCurrentAndFutureAssignedEmployees(
                timeOffPolicy.currentNonTerminatedEmployeeTimeOffPolicies,
                timeOffPolicy.futureAssignedNonTerminatedEmployeeTimeOffPolicies
            );

            this.rows = this.assignedEmployees.map(mapEmployeesToRow);
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private combineCurrentAndFutureAssignedEmployees(
        currentlyAssignedNonTerminatedEmployees: EmployeeTimeOffPolicies[],
        futureAssignedNonTerminatedEmployees: EmployeeTimeOffPolicies[]
    ): EmployeeTimeOffPolicies[] {
        // Go over each to mark these as scheduled employees
        futureAssignedNonTerminatedEmployees.forEach((employeeTimeOffPolicy: EmployeeTimeOffPolicies) => {
            employeeTimeOffPolicy.simpleEmployee.scheduledForFuturePolicyAssignment = true;
        });
        // Combine them into one array
        return [...currentlyAssignedNonTerminatedEmployees, ...futureAssignedNonTerminatedEmployees];
    }

    onSearch(searchQuery: string | null): void {
        if (searchQuery === null) {
            this.rows = this.assignedEmployees.map(mapEmployeesToRow);
            return;
        }

        const nameIncludesSearchQuery = (row: Row): boolean =>
            (row.cells[0] as { employee: Employee; isAvatar: boolean }).employee.fullName
                .toLowerCase()
                .includes(searchQuery.toLocaleLowerCase());

        this.rows = this.assignedEmployees.map(mapEmployeesToRow).filter(nameIncludesSearchQuery);
    }
}
